import { UserRoles } from "./enums";

export enum Permission {
  MANAGE_ORGANIZATION = "manage_organization",
  MANAGE_USERS = "manage_users",
  MANAGE_BILLING = "manage_billing",
  MANAGE_INTEGRATIONS = "manage_integrations",
  MANAGE_PHONE_NUMBERS = "manage_phone_numbers",
  MANAGE_CONTACTS = "manage_contacts",
  DELETE_RECORDS = "delete_records",
  IMPORT_EXPORT = "import_export",
  MANAGE_TEMPLATES = "manage_templates",
  MANAGE_SEQUENCES = "manage_sequences",
  COMMUNICATION = "communication",
  VIEW_REPORTS = "view_reports",
  BASIC_ACCESS = "basic_access",
}

export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  [UserRoles.super_admin]: Object.values(Permission),
  [UserRoles.owner]: Object.values(Permission),
  [UserRoles.admin]: [
    Permission.MANAGE_ORGANIZATION,
    Permission.MANAGE_USERS,
    Permission.MANAGE_INTEGRATIONS,
    Permission.MANAGE_PHONE_NUMBERS,
    Permission.MANAGE_CONTACTS,
    Permission.DELETE_RECORDS,
    Permission.IMPORT_EXPORT,
    Permission.MANAGE_TEMPLATES,
    Permission.MANAGE_SEQUENCES,
    Permission.COMMUNICATION,
    Permission.VIEW_REPORTS,
    Permission.BASIC_ACCESS,
  ],
  [UserRoles.user]: [
    Permission.MANAGE_INTEGRATIONS,
    Permission.MANAGE_CONTACTS,
    Permission.MANAGE_TEMPLATES,
    Permission.MANAGE_SEQUENCES,
    Permission.COMMUNICATION,
    Permission.VIEW_REPORTS,
    Permission.BASIC_ACCESS,
  ],
  [UserRoles.restricted]: [Permission.BASIC_ACCESS],
};

export const PERMISSION_DISPLAY: Record<
  Permission,
  { label: string; description: string; icon: string; category: string }
> = {
  [Permission.MANAGE_ORGANIZATION]: {
    label: "Manage Organization",
    description: "Org settings, customizations, bucket options, opportunity stages, lead sources",
    icon: "mdi:cog",
    category: "organization",
  },
  [Permission.MANAGE_USERS]: {
    label: "Manage Users",
    description: "Invite users, edit roles, deactivate/delete users",
    icon: "mdi:account-group",
    category: "organization",
  },
  [Permission.MANAGE_BILLING]: {
    label: "Manage Billing",
    description: "Subscriptions, payment methods, checkout, promo codes, seats",
    icon: "mdi:credit-card-outline",
    category: "organization",
  },
  [Permission.MANAGE_INTEGRATIONS]: {
    label: "Manage Integrations",
    description: "CRM sync, channels, meeting providers, connect/disconnect",
    icon: "mdi:link-variant",
    category: "organization",
  },
  [Permission.MANAGE_PHONE_NUMBERS]: {
    label: "Manage Phone Numbers",
    description: "Purchase/delete phone numbers, manage rotations",
    icon: "mdi:phone-outline",
    category: "organization",
  },
  [Permission.MANAGE_CONTACTS]: {
    label: "Manage Contacts",
    description: "Create/edit contacts, companies, buckets, opportunities, meetings, notes",
    icon: "mdi:account-edit-outline",
    category: "data",
  },
  [Permission.DELETE_RECORDS]: {
    label: "Delete Records",
    description: "Delete contacts, companies, buckets, opportunities, call logs",
    icon: "mdi:delete-outline",
    category: "data",
  },
  [Permission.IMPORT_EXPORT]: {
    label: "Import & Export",
    description: "CSV import, data export (contacts, companies, buckets, meetings, reports)",
    icon: "mdi:swap-vertical",
    category: "data",
  },
  [Permission.MANAGE_TEMPLATES]: {
    label: "Manage Templates",
    description: "Create/edit/delete email & SMS templates, template folders",
    icon: "mdi:file-document-edit-outline",
    category: "features",
  },
  [Permission.MANAGE_SEQUENCES]: {
    label: "Manage Sequences",
    description: "Create/edit/delete email sequences and steps, enroll/pause/resume/exit contacts",
    icon: "mdi:email-fast-outline",
    category: "features",
  },
  [Permission.COMMUNICATION]: {
    label: "Communication",
    description: "Make calls, send emails, send SMS, LinkedIn messaging",
    icon: "mdi:message-text-outline",
    category: "features",
  },
  [Permission.VIEW_REPORTS]: {
    label: "View Reports",
    description: "Dashboard stats, analytics, AI insights",
    icon: "mdi:chart-bar",
    category: "features",
  },
  [Permission.BASIC_ACCESS]: {
    label: "Basic Access",
    description: "View own contacts, opportunities, buckets, notifications, search",
    icon: "mdi:eye-outline",
    category: "features",
  },
};

export const PERMISSION_CATEGORIES = [
  {
    key: "organization",
    label: "Organization & Admin",
    permissions: [
      Permission.MANAGE_ORGANIZATION,
      Permission.MANAGE_USERS,
      Permission.MANAGE_BILLING,
      Permission.MANAGE_INTEGRATIONS,
      Permission.MANAGE_PHONE_NUMBERS,
    ],
  },
  {
    key: "data",
    label: "Data Management",
    permissions: [Permission.MANAGE_CONTACTS, Permission.DELETE_RECORDS, Permission.IMPORT_EXPORT],
  },
  {
    key: "features",
    label: "Features & Communication",
    permissions: [
      Permission.MANAGE_TEMPLATES,
      Permission.MANAGE_SEQUENCES,
      Permission.COMMUNICATION,
      Permission.VIEW_REPORTS,
      Permission.BASIC_ACCESS,
    ],
  },
];

export const hasPermission = (
  role: string,
  permission: Permission,
  orgPermissions?: Record<string, Permission[]> | null
): boolean => {
  const permissions = orgPermissions?.[role] ?? ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  return permissions.includes(permission);
};
